import { Divider } from 'src/components/atoms/Divider';
import { StyledSubheader } from 'src/components/foundations/foundations.styles';
import { updatePermissions } from 'src/components/pages/Case/Case.slice';
import { CasePermissionsType, CaseTypeEnum } from 'src/types';
import { usePermissionLogic } from '../../hooks/usePermissionLogic';
import {
  AccessItemPII,
  AccessItemPIIContainer,
  AccessItemTitle,
  AccessStyleBody,
  AccessTypeContainer,
  DividerContainer,
  PIISectionItem,
  PIISectionItemWrappper,
  StyledCheckbox,
} from '../../Steps/AccessStep.styles';
import { AccessActionEnum, AccessItem } from '../AccessItem/AccessItem';
import { RefinedAccessHeader } from './Access.styles';

interface AccessProps {
  permissions: CasePermissionsType[];
  handleSetPermissions: (
    permissions: CasePermissionsType[],
    action: AccessActionEnum,
  ) => void;
  disabledViewCasePII: boolean;
  disabledViewReportPII: boolean;
  unableToChat?: boolean;
  disablePermissions: boolean;
  isReportAssignee?: boolean;
  isSuperAdmin?: boolean;
  caseType?: CaseTypeEnum;
  newEnv?: boolean;
}

export const Access = ({
  permissions,
  handleSetPermissions,
  disabledViewCasePII,
  disabledViewReportPII,
  unableToChat,
  disablePermissions = false,
  isReportAssignee = false,
  isSuperAdmin = false,
  newEnv = false,
  caseType = CaseTypeEnum.SPEAK_UP,
}: AccessProps) => {
  const disablePermissionForSuperOrAssignee =
    disablePermissions || isSuperAdmin || isReportAssignee;

  const { updatePermissionsWithConsistency } = usePermissionLogic({
    permissions,
    handleSetPermissions,
  });

  const handlePIISelection = () => {
    const shouldAddAll = !(hasViewCasePII && hasViewReportPII);
    if (shouldAddAll) {
      const permissionsToAdd: CasePermissionsType[] = [];
      if (!hasViewCasePII && !disabledViewCasePII) {
        permissionsToAdd.push('VIEW_CASE_DETAILS_PII');
      }
      if (!hasViewReportPII && !disabledViewReportPII) {
        permissionsToAdd.push('VIEW_REPORT_DETAILS_PII');
      }
      permissionsToAdd.push('VIEW_CASE_AUDIT_LOGS_TAB');
      if (permissionsToAdd.length > 0) {
        updatePermissionsWithConsistency(
          permissionsToAdd,
          AccessActionEnum.ADD,
        );
      }
    } else {
      const permissionsToRemove: CasePermissionsType[] = [
        'VIEW_CASE_AUDIT_LOGS_TAB',
      ];
      if (permissions.includes('VIEW_CASE_DETAILS_PII')) {
        permissionsToRemove.push('VIEW_CASE_DETAILS_PII');
      }
      if (permissions.includes('VIEW_REPORT_DETAILS_PII')) {
        permissionsToRemove.push('VIEW_REPORT_DETAILS_PII');
      }
      if (permissionsToRemove.length > 0) {
        updatePermissionsWithConsistency(
          permissionsToRemove,
          AccessActionEnum.REMOVE,
        );
      }
    }
  };

  const hasViewCasePII =
    permissions.includes('VIEW_CASE_AUDIT_LOGS_TAB') ||
    permissions.includes('VIEW_CASE_DETAILS_PII');
  const hasViewReportPII =
    permissions.includes('VIEW_CASE_AUDIT_LOGS_TAB') ||
    permissions.includes('VIEW_REPORT_DETAILS_PII');

  const handlePIIReadOnlySelection = (
    permissionsToUpdate: CasePermissionsType[],
    action: AccessActionEnum,
  ) => {
    updatePermissionsWithConsistency(permissionsToUpdate, action);
  };

  const handleCasePIIToggle = () => {
    if (hasViewCasePII) {
      const currentPermissionsAfterRemoval = updatePermissions(
        permissions,
        ['VIEW_CASE_DETAILS_PII'],
        AccessActionEnum.REMOVE,
      );

      const permissionsToRemove = permissions.filter(
        (permission) => !currentPermissionsAfterRemoval.includes(permission),
      );

      if (permissionsToRemove.length > 0) {
        handleSetPermissions(permissionsToRemove, AccessActionEnum.REMOVE);
      }
    } else {
      updatePermissionsWithConsistency(
        ['VIEW_CASE_DETAILS_PII'],
        AccessActionEnum.ADD,
      );
    }
  };

  const handleReportPIIToggle = () => {
    if (hasViewReportPII) {
      const currentPermissionsAfterRemoval = updatePermissions(
        permissions,
        ['VIEW_REPORT_DETAILS_PII'],
        AccessActionEnum.REMOVE,
      );

      const permissionsToRemove = permissions.filter(
        (permission) => !currentPermissionsAfterRemoval.includes(permission),
      );

      if (permissionsToRemove.length > 0) {
        handleSetPermissions(permissionsToRemove, AccessActionEnum.REMOVE);
      }
    } else {
      updatePermissionsWithConsistency(
        ['VIEW_REPORT_DETAILS_PII'],
        AccessActionEnum.ADD,
      );
    }
  };

  const workspaceTitle =
    caseType === CaseTypeEnum.SPEAK_UP
      ? 'Workspace'
      : 'Workspace tab and mitigation plans';
  const workspaceDescription =
    caseType === CaseTypeEnum.SPEAK_UP
      ? 'Including all files uploaded and attached to the case, as well as documents created in Vault.'
      : 'Including all files uploaded and attached to the case, as well as assigning mitigation plans.';

  return (
    <>
      {!newEnv && (
        <>
          <RefinedAccessHeader>
            <StyledSubheader>{'Refine access'}</StyledSubheader>
            <AccessTypeContainer>
              <AccessStyleBody>Read-only</AccessStyleBody>
              <AccessStyleBody>Editor</AccessStyleBody>
            </AccessTypeContainer>
          </RefinedAccessHeader>

          <DividerContainer>
            <Divider variant={'horizontal'} />
          </DividerContainer>
        </>
      )}
      {caseType === CaseTypeEnum.SPEAK_UP && (
        <AccessItem
          selectedPermissions={permissions}
          readPermission={'VIEW_CASE_DETAILS'}
          editPermission={'EDIT_CASE_DETAILS'}
          handleSetPermissions={handleSetPermissions}
          disabled={disablePermissions || isSuperAdmin}
          readPermissionDisabled={isReportAssignee}
        >
          <AccessItemTitle>Case details</AccessItemTitle>
          <AccessStyleBody>
            Including case status, case category, case dates (created, read,
            investigated and closed).
          </AccessStyleBody>
        </AccessItem>
      )}

      {caseType === CaseTypeEnum.SPEAK_UP && (
        <AccessItem
          selectedPermissions={permissions}
          readPermission={'VIEW_REPORT_DETAILS'}
          editPermission={'EDIT_REPORT_DETAILS'}
          handleSetPermissions={handleSetPermissions}
          disabled={disablePermissionForSuperOrAssignee}
        >
          <AccessItemTitle>Report details</AccessItemTitle>
          <AccessStyleBody>
            Including submission date, reported/determined category,
            substantiation status, and reporter information.
          </AccessStyleBody>
        </AccessItem>
      )}

      {caseType === CaseTypeEnum.COI && (
        <AccessItem
          selectedPermissions={permissions}
          readPermission={'VIEW_CASE_DETAILS'}
          editPermission={'EDIT_CASE_DETAILS'}
          handleSetPermissions={handleSetPermissions}
          disabled={disablePermissions || isSuperAdmin}
          readPermissionDisabled={isReportAssignee}
        >
          <AccessItemTitle>Report and audit log tab</AccessItemTitle>
          <AccessStyleBody>
            Every information found under the tabs
          </AccessStyleBody>
        </AccessItem>
      )}

      <AccessItem
        selectedPermissions={permissions}
        readPermission={'VIEW_REPORT_MESSAGES'}
        editPermission={'SEND_REPORT_MESSAGES'}
        handleSetPermissions={handleSetPermissions}
        disabled={disablePermissions || unableToChat || isReportAssignee}
        readPermissionDisabled={isSuperAdmin}
      >
        <AccessItemTitle>Reporter messages</AccessItemTitle>
        <AccessStyleBody>
          Including all messages exchanged between the case lead and the
          reporter.
        </AccessStyleBody>
      </AccessItem>
      {caseType === CaseTypeEnum.SPEAK_UP && (
        <AccessItem
          selectedPermissions={permissions}
          readPermission={'VIEW_CASE_AUDIT_LOGS_TAB'}
          handleSetPermissions={handlePIIReadOnlySelection}
          disabled={disablePermissionForSuperOrAssignee}
        >
          <AccessItemTitle>
            Personally identifying information (PII) (Read-only)
          </AccessItemTitle>
          <AccessItemPIIContainer>
            <StyledCheckbox
              checked={hasViewCasePII && hasViewReportPII}
              data-testid="VIEW_ALL_PII"
              disabled={disablePermissionForSuperOrAssignee}
              onChange={handlePIISelection}
            />
            <AccessStyleBody>
              Includes full access to the audit log, case level PII and all
              report level PII.
            </AccessStyleBody>
          </AccessItemPIIContainer>

          <PIISectionItem>
            <PIISectionItemWrappper>
              <>
                <StyledCheckbox
                  checked={hasViewCasePII}
                  data-testid="VIEW_CASE_DETAILS_PII"
                  disabled={disablePermissionForSuperOrAssignee}
                  onChange={handleCasePIIToggle}
                />
              </>
              <AccessItemPII>
                Show personally identifying information (PII) at case level only
              </AccessItemPII>
            </PIISectionItemWrappper>
          </PIISectionItem>
          <PIISectionItem>
            <PIISectionItemWrappper>
              <StyledCheckbox
                checked={hasViewReportPII}
                data-testid="VIEW_REPORT_DETAILS_PII"
                disabled={disablePermissionForSuperOrAssignee}
                onChange={handleReportPIIToggle}
              />
              <AccessItemPII>
                Show personally identifying information (PII) at report level
                only
              </AccessItemPII>
            </PIISectionItemWrappper>
          </PIISectionItem>
        </AccessItem>
      )}
      <AccessItem
        selectedPermissions={permissions}
        readPermission={'VIEW_CASE_WORKSPACE_TAB'}
        editPermission={'EDIT_CASE_WORKSPACE_TAB'}
        handleSetPermissions={handleSetPermissions}
        disabled={disablePermissionForSuperOrAssignee}
      >
        <AccessItemTitle>{workspaceTitle}</AccessItemTitle>
        <AccessStyleBody>{workspaceDescription}</AccessStyleBody>
      </AccessItem>
    </>
  );
};
